import PictureViewDialog from "../framework/pictureViewDialog";
import PictureOptionDialog from "../framework/pictureOptionDialog";
import { DriveStore } from "../../store/reducers";
import Translator from "../../utils/translator";
import { IImageOptions } from "../../model/Image";
import { getFileController } from "../../utils/native/nativeFileApi";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import { getAppType } from "../../utils/native/nativeHelpers";
import { MINUTES } from "../../constants";
import { getServerDate } from "../../utils/storage/date";
export { convertToJpeg } from "../../utils/imageUtils";

interface ICacheEntry {
    data: string;
    timestamp: number;
}

const MAX_CACHE_SIZE = 20;
const CACHE_EXPIRY_MS = 20 * MINUTES;
const imageCache: Map<string, ICacheEntry> = new Map();

/**
 * Clears expired entries from the image cache based on timestamp and enforces size limit.
 * Called periodically by the scheduler and after cache additions to prevent stale data accumulation.
 */
export function clearExpiredImageCache(): void {
    const now = getServerDate().getTime();
    const keysToDelete: string[] = [];

    imageCache.forEach((entry, key) => {
        if ((now - entry.timestamp) >= CACHE_EXPIRY_MS) {
            keysToDelete.push(key);
        }
    });

    keysToDelete.forEach(key => imageCache.delete(key));

    // FIFO eviction — remove oldest entries if over limit
    if (imageCache.size > MAX_CACHE_SIZE) {
        const allKeys = Array.from(imageCache.keys());
        const keysToEvict = allKeys.slice(0, imageCache.size - MAX_CACHE_SIZE);
        keysToEvict.forEach(key => imageCache.delete(key));
    }
}

export function openPictureViewDialog(
    e: Event,
    store: DriveStore,
    cameraIconId: string,
    filePath: string,
    translator: Translator,
    onlyViewImage = false,
    isTrailer = false
): void {
    dispatcher.trigger(DispatcherEvents.DriveAppInteraction, e);
    let pictureDialog = new PictureViewDialog(translator, store, filePath, cameraIconId, isTrailer);
    void pictureDialog.open(onlyViewImage);
}

export function getCameraIcon(): string {
    return `<svg class="svgIcon bigger bottom-margin" role="img" aria-label="Camera icon">
    <use xlink:href="#drive-camera"></use>
    </svg>`;
}

export function openPictureOptionDialog(e: Event, store: DriveStore, cameraIconId: string, translator: Translator, isTrailer: boolean): void {
    dispatcher.trigger(DispatcherEvents.DriveAppInteraction, e);
    let pictureDialog = new PictureOptionDialog(translator, store, isTrailer);
    void pictureDialog.open(cameraIconId, undefined);
}

export function updateCameraIconWithImage(imageOptions: IImageOptions, store: DriveStore, translator: Translator, isTrailer = false) {
    let cameraIconId: string = imageOptions.cameraIconId;
    let cameraIcon = document.getElementById(cameraIconId);
    if (cameraIcon) {
        if (imageOptions.deleteImage) {
            cameraIcon.className =  cameraIcon.className === "inspectionListImage" ? "cameraInspectIcon" : "cameraRepairIcon";
            cameraIcon.innerHTML = getCameraIcon();
            cameraIcon.onclick = e => openPictureOptionDialog(e, store, cameraIconId, translator, isTrailer) ;
            return;
        }
        cameraIcon.className = cameraIcon.className === "cameraInspectIcon" ? "inspectionListImage" : "newImageOnRepairView";
        let ariaLabel = cameraIcon.className === "inspectionListImage" ? "Inspection camera icon" : "Repair camera icon";
        cameraIcon.setAttribute("filePath", imageOptions.filePath);
        cameraIcon.innerHTML = `<img src="${ imageOptions.base64Image }" aria-label="${ ariaLabel }">`;
        cameraIcon.onclick = e => openPictureViewDialog(e, store, cameraIconId, imageOptions.filePath, translator, isTrailer);
    }
}

/**
 * Pre-warms the image cache with a base64 value obtained from a source other than disk
 * (e.g. the camera capture callback). This allows subsequent getCachedBase64Data calls
 * for the same file to return immediately from cache rather than hitting the native bridge.
 */
export function prewarmImageCache(filePath: string, base64: string): void {
    imageCache.set(filePath, { data: base64, timestamp: getServerDate().getTime() });
}

/**
 * Cached wrapper for getBase64Data that uses FIFO cache with time-based expiry.
 * Cache has a hard limit of 20 entries and entries expire after 20 minutes.
 */
export function getCachedBase64Data(filePath: string): Promise<string> {
    const now = getServerDate().getTime();
    const cacheEntry = imageCache.get(filePath);

    if (cacheEntry && (now - cacheEntry.timestamp) < CACHE_EXPIRY_MS) {
        return Promise.resolve(cacheEntry.data);
    }

    return getFileController().getBase64Data(filePath).then((base64Data) => {
        imageCache.set(filePath, {
            data: base64Data,
            timestamp: now
        });

        clearExpiredImageCache();

        return base64Data;
    });
}

export function getCachedImageForRemark(remarkWithMediaId: string, mediaFileId: string, store: DriveStore, translator: Translator): Promise<void> {
    let mediaFiles = store.getState().data.images;
    let mediaFile = mediaFiles[mediaFileId];
    if (!mediaFile) {
        return Promise.resolve();
    }

    if (!mediaFile.path) {
        return Promise.resolve();
    }

    return getCachedBase64Data(mediaFile.path).then((base64Data) => {
        let cameraSpanElements = document.querySelectorAll(`[id*=${ remarkWithMediaId }]`);
        if (!cameraSpanElements) {
            return;
        }
        Array.prototype.forEach.call(cameraSpanElements, element => {
            element.innerHTML = `<img src="${ base64Data }" aria-label="Image For Defect Remark">`;
            // TODO: This could be a memory leak with references outside of the event listener?
            element.addEventListener("click", (e: Event) => {
                e.stopImmediatePropagation();
                openPictureViewDialog(e, store, remarkWithMediaId, mediaFile.path, translator, true);
            });
        });
    }).catch(() => {
        throw new Error(`Could not retrieve image data. AppType: ${ getAppType() }`);
    });
}

